// Compass / Mountain panel help (the "?" beside the gear on the compass screen). Plain words;
// keep it in step with the screen (v10/LifeView.tsx, olmps/CompassView.tsx, v10/MountainView.tsx).
import { SECTOR_COLOR } from '../../v10/theme';
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export function compassHelp(colors: { onPace: string; behind: string; lapsed: string; faint: string }): ScreenHelpContent {
  return {
    title: 'Compass and Mountain',
    intro:
      "This is the map of your life. The compass shows all five areas at once. The mountain shows one area at a time as a climb. Both show the same things: where you are and whether you are on track. Tap anything on it to see what it holds.",
    sections: [
      {
        heading: 'The five areas',
        body: ['Each slice of the compass, and each face of the mountain, is one area of your life.'],
        items: [
          { term: 'Social', text: 'the people in your life.', color: SECTOR_COLOR.S },
          { term: 'Health', text: 'your body.', color: SECTOR_COLOR.H },
          { term: 'Admin', text: 'running your life.', color: SECTOR_COLOR.A },
          { term: 'Personal', text: 'your inner life.', color: SECTOR_COLOR.P },
          { term: 'Economic', text: 'work and money.', color: SECTOR_COLOR.E },
        ],
      },
      {
        heading: 'The rings',
        body: [
          'Each area has five dots, one on each ring. From the middle out they are Endeavour, Goal, Aim, Today and Step: the big direction first, the very next action last.',
          'Tap a dot. A card appears with the area, the ring, what you set there, and how you are doing. Tap "Explore ▼" on the card to open that ring in the orb below. Tap an area name to open its overview.',
          'On the mountain the same five stops climb up the face, with Endeavour at the top and Step at the bottom. Tap a stop to see its card, or tap an area name to turn the mountain to that face.',
        ],
      },
      {
        heading: 'Colours',
        body: ['The edge of each dot shows how that ring is doing. A dot that is behind also drifts to the left of its line, so you can see it at a glance.'],
        items: [
          { term: 'Green', text: 'on its line. You are on track.', color: colors.onPace },
          { term: 'Amber', text: 'a little behind.', color: colors.behind },
          { term: 'Red', text: 'a long way behind.', color: colors.lapsed },
          { term: 'Grey', text: 'not set up yet. Nothing is held against you.', color: colors.faint },
        ],
      },
      {
        heading: 'The fill',
        body: [
          "In Fill mode, each area's colour fills its slice as more of its work gets done. It fills the outer ring first and works inward, one fifth at a time. Clear mode shows the plain compass with no fill.",
          'Under the compass hang your projects (TKNE). Each project is a row with its name at the end. Each dot in the row is one step: faded means not started, glowing means started, solid means done. Small dots below a step are its sub-steps. A project name turns red when it is behind.',
        ],
      },
      {
        heading: 'The gear button',
        body: ['Tap the gear to change how the screen looks. It does not change your data.'],
        items: [
          { term: 'View', text: 'Compass or Mountain.' },
          { term: 'Disc', text: 'Fill or Clear. Only on the compass.' },
          { term: 'Projects', text: 'TKNE shows the projects under each area. SOLO makes one slice for each active project instead of one for each area. Off hides them and flattens the view.' },
        ],
      },
      {
        heading: 'The little pad',
        body: [
          'On the compass: ▲ and ▼ tilt it, ◀ and ▶ turn it, the middle button stops or starts the slow spin, and − and + zoom. Tap ▲ or ▼ three times fast to jump all the way to flat or to the side view.',
          'On the mountain: ◀ and ▶ go to the next face, ▲ and ▼ move up and down the stops, and the middle button spins the mountain. You can also drag the mountain to turn it.',
          "At the top right of the screen is a face: the deity of the area you are looking at. Tap it to call it, video it, chat with it, or hear every area's rundown with Play all.",
        ],
      },
      {
        heading: 'Moving around',
        items: [
          { term: '▲', text: 'up to the Pantheon.' },
          { term: '▼', text: 'down to the orb.' },
          { term: '◀', text: 'left to the Dashboard.' },
          { term: '▶', text: 'right to NUMA.' },
        ],
      },
    ],
  };
}
